import { useState, useCallback, useEffect, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { tps, getTpsToken, subscribeTpsToken, TPSConnectionError, TPSError, TPSPairingRequiredError } from "@/lib/tps";
import { diagnoseUnreachable, environmentProblem, type TpsProblem } from "@/lib/tpsEnvironment";
import { getSelectedContext } from "@/lib/api";
import type { PrinterAssignments, TPSPrinter } from "@/types/printer";
import { PRINTER_ASSIGNMENTS_KEY_PREFIX } from "@/types/printer";

// ==========================================
// localStorage helpers for printer assignments
// ==========================================

function getStorageKey(): string {
  const context = getSelectedContext();
  const outletId = context?.outlet?.id ?? "default";
  return `${PRINTER_ASSIGNMENTS_KEY_PREFIX}${outletId}`;
}

/**
 * This device's bill / KOT printers for the current outlet, as stored now.
 * Read it when printing (not once at mount): a printer may be assigned since.
 */
export function loadAssignments(): PrinterAssignments {
  try {
    const stored = localStorage.getItem(getStorageKey());
    if (stored) return JSON.parse(stored);
  } catch {
    /* ignore parse errors */
  }
  return { bill_printer_id: null, kot_printer_id: null };
}

function saveAssignments(assignments: PrinterAssignments): void {
  localStorage.setItem(getStorageKey(), JSON.stringify(assignments));
}

// ==========================================
// Connection to the Dhaba Print Service
// ==========================================

/**
 * - `unsupported`: this page can't use the print service (Safari, iPhone/iPad, or a site
 *   other than billing.dhaba.ai) — see `problem`.
 * - `checking`: first answer not in yet.
 * - `not-paired`: the service is running; this browser must be connected (paired) first.
 * - `unreachable`: nothing answered — see `problem`.
 * - `error`: the service answers, but with errors (a 429, a 500) — see `errorMessage`.
 * - `connected`: paired; printers listed.
 */
export type TPSConnectionState = "unsupported" | "checking" | "not-paired" | "unreachable" | "error" | "connected";

export const TPS_PRINTERS_QUERY_KEY = ["tps-printers"] as const;
export const TPS_PING_QUERY_KEY = ["tps-ping"] as const;

/** After this many failed printer-list checks in a row, stop showing the last list as connected. */
const PRINTER_FAILURES_BEFORE_ERROR = 3;
/** Failed printer-list checks in a row that were answers (429, 500), not network failures. */
let printerFailureStreak = 0;

async function fetchPrinters(): Promise<TPSPrinter[]> {
  try {
    const res = await tps.getPrinters();
    printerFailureStreak = 0;
    return res.data.printers;
  } catch (error) {
    if (error instanceof TPSError && !(error instanceof TPSPairingRequiredError)) printerFailureStreak += 1;
    throw error;
  }
}

type PingResult = { reachable: true; version: string } | { reachable: false; problem: TpsProblem };

async function pingService(): Promise<PingResult> {
  try {
    const res = await tps.ping();
    return { reachable: true, version: res.data.version };
  } catch (error) {
    if (error instanceof TPSConnectionError) return { reachable: false, problem: await diagnoseUnreachable() };
    throw error;
  }
}

export function useTPSConnection() {
  const queryClient = useQueryClient();
  const token = useSyncExternalStore(subscribeTpsToken, getTpsToken, () => null);
  const envProblem = environmentProblem();
  const unsupported = envProblem !== null;

  // --- Printer list (polled while paired) ---
  const printersQuery = useQuery({
    queryKey: [...TPS_PRINTERS_QUERY_KEY, token],
    queryFn: fetchPrinters,
    enabled: !unsupported && !!token,
    refetchInterval: 10_000,
    retry: (failureCount, error) => error instanceof TPSConnectionError && failureCount < 1,
    retryDelay: 2000,
    refetchOnWindowFocus: true,
  });

  const printersUnreachable = printersQuery.error instanceof TPSConnectionError;

  // --- Is the service there at all? (while not paired, or when the printer list can't be fetched) ---
  const pingQuery = useQuery({
    queryKey: TPS_PING_QUERY_KEY,
    queryFn: pingService,
    enabled: !unsupported && (!token || printersUnreachable),
    refetchInterval: 15_000,
    retry: false,
    refetchOnWindowFocus: true,
  });

  // The service answered the printer list with an error (not "pair first", not a network failure).
  const printersFailing =
    !!token &&
    printersQuery.isError &&
    printersQuery.error instanceof TPSError &&
    !(printersQuery.error instanceof TPSPairingRequiredError);

  let state: TPSConnectionState;
  let problem: TpsProblem | null = null;
  let errorMessage: string | null = null;
  if (unsupported) {
    state = "unsupported";
    problem = envProblem;
  } else if (
    token &&
    printersQuery.data !== undefined &&
    !printersUnreachable &&
    !(printersFailing && printerFailureStreak >= PRINTER_FAILURES_BEFORE_ERROR)
  ) {
    // Keeps the last list through a passing error (a 429, a 500) until the next poll.
    state = "connected";
  } else if (printersFailing) {
    state = "error";
    errorMessage = (printersQuery.error as TPSError).message;
  } else if (token && !printersUnreachable) {
    state = "checking";
  } else if (pingQuery.data?.reachable === false) {
    state = "unreachable";
    problem = pingQuery.data.problem;
  } else if (pingQuery.data?.reachable === true) {
    // Reachable: not paired yet (a paired browser is still waiting for its printer list).
    state = token ? "checking" : "not-paired";
  } else if (pingQuery.error instanceof TPSError) {
    state = "error";
    errorMessage = pingQuery.error.message;
  } else if (pingQuery.isError) {
    state = "unreachable";
    problem = "not-running";
  } else {
    state = "checking";
  }

  const serviceVersion = pingQuery.data?.reachable === true ? pingQuery.data.version : null;

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TPS_PING_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: TPS_PRINTERS_QUERY_KEY }),
    ]);
  }, [queryClient]);

  const disconnect = useCallback(async () => {
    await tps.unpair();
    queryClient.removeQueries({ queryKey: TPS_PRINTERS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: TPS_PING_QUERY_KEY });
  }, [queryClient]);

  return {
    state,
    problem,
    errorMessage,
    serviceVersion,
    isConnected: state === "connected",
    isConnecting: state === "checking",
    needsPairing: state === "not-paired",
    /** Kept for callers that only need "is something wrong with the connection". */
    connectionError:
      state === "unreachable" || state === "unsupported" ? new TPSConnectionError() : null,
    printers: state === "connected" ? (printersQuery.data ?? []) : [],
    refetchPrinters: refresh,
    disconnect,
  };
}

// ==========================================
// Hooks
// ==========================================

/**
 * This outlet's bill/KOT printer on this device, without talking to the print service.
 * For screens that only print (Orders, Settlement, KOT history): sendToPrinter reports
 * any connection problem when a print is attempted.
 */
export function usePrinterAssignments() {
  const [assignments, setAssignments] =
    useState<PrinterAssignments>(loadAssignments);

  // Sync to localStorage whenever assignments change
  useEffect(() => {
    saveAssignments(assignments);
  }, [assignments]);

  const assignPrinter = useCallback(
    (role: "bill" | "kot", printerId: string | null) => {
      setAssignments((prev) => ({
        ...prev,
        [role === "bill" ? "bill_printer_id" : "kot_printer_id"]: printerId,
      }));
    },
    []
  );

  return { assignments, assignPrinter };
}

/** Connection state, printers (polled) and assignments — for Print settings. */
export function useTPS() {
  const connection = useTPSConnection();
  const { assignments, assignPrinter } = usePrinterAssignments();

  const { printers } = connection;

  return {
    ...connection,

    // Assignments (persisted in localStorage)
    assignments,
    assignPrinter,

    // Derived: resolved printer objects for each role
    billPrinter:
      printers.find((p) => p.id === assignments.bill_printer_id) ?? null,
    kotPrinter:
      printers.find((p) => p.id === assignments.kot_printer_id) ?? null,
  };
}

